import * as z from 'zod';

import { LosId } from '../../BrandedIds';

const emptyToUndefined = (v?: string) => (v === '' ? undefined : v);

/**
 * One taxing-authority block from a CoreLogic "Return ADDs" record (up to 5 per
 * record). `taxingAuthorityNumber` is the CoreLogic payee identifier — our
 * Payee.externalPayeeId. `taxIdentificationNumber` is the parcel number / APN —
 * the match key against TaxEscrowSetting.apn.
 */
export const CorelogicTaxingAuthority = z.object({
  taxingAuthorityCode: z.string().trim().optional().transform(emptyToUndefined),
  pendingApportionmentHitCode: z.string().trim().optional().transform(emptyToUndefined),
  taxingAuthorityNumber: z.string().trim().min(1, 'Taxing Authority Number is required'),
  taxingAuthorityName: z.string().trim().optional().transform(emptyToUndefined),
  taxingAuthorityType: z.string().trim().optional().transform(emptyToUndefined),
  taxRateArea: z.string().trim().optional().transform(emptyToUndefined),
  taxIdentificationNumber: z.string().trim().min(1, 'Tax Identification Number (APN) is required'),
});
export type CorelogicTaxingAuthority = z.infer<typeof CorelogicTaxingAuthority>;

/**
 * A single parsed record from a CoreLogic "Return ADDs" file (a fixed-width
 * 1500-byte layout). The byte-offset parser lives in
 * `packages/shared-node/src/fileImport/corelogic` and produces the raw,
 * field-name-keyed object this schema validates.
 *
 * The file carries NO tax amounts — CoreLogic returns it after receiving an
 * ADDS file, with the assigned contract number and the taxing authorities it
 * placed under contract. Only the fields the importer actually consumes are
 * modeled here.
 */
export const CorelogicReturnAddsRecord = z.object({
  recordType: z
    .string()
    .trim()
    .refine((v) => v === '1', 'Record Type must be "1"'),
  contractNumber: z.string().trim().optional().transform(emptyToUndefined),
  // C = escrow, B = non-escrow. B records are ignored by the importer.
  serviceType: z.string().trim().optional(),
  loanNumber: z
    .string()
    .trim()
    .min(1, 'Loan Number is required')
    .transform((v) => v as LosId),
  taxingAuthorities: z.array(CorelogicTaxingAuthority).max(5),
});
export type CorelogicReturnAddsRecord = z.infer<typeof CorelogicReturnAddsRecord>;
